const MAX_EXERCISES = 60;
const MAX_EXERCISE_GROUPS = 12;
const MAX_EQUIPMENT = 8;
const MAX_TIPS = 8;
const DIFFICULTIES = new Set(['easy', 'medium', 'hard']);

const asTrimmedString = (value, maxLength = 160) => (
  typeof value === 'string' ? value.trim().slice(0, maxLength) : ''
);

const asShortValue = (value, maxLength = 80) => {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return asTrimmedString(value, maxLength);
};

const asOptionalList = (value, maxItems, maxItemLength) => (
  Array.isArray(value)
    ? value.map(item => asTrimmedString(item, maxItemLength)).filter(Boolean).slice(0, maxItems)
    : []
);

// Accepts a string ("Push-ups") or an object ({ name, sets, reps, rest, notes }).
const parseExercise = (entry) => {
  if (typeof entry === 'string') {
    const name = asTrimmedString(entry, 80);
    return name ? { name, sets: '', reps: '', rest: '', notes: '' } : null;
  }
  if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
    const name = asTrimmedString(entry.name, 80);
    if (!name) return null;
    return {
      name,
      sets: asShortValue(entry.sets, 40),
      reps: asShortValue(entry.reps, 40),
      rest: asShortValue(entry.rest, 40),
      notes: asTrimmedString(entry.notes, 200),
    };
  }
  return null;
};

// Exercises arrive either as flat entries or as { section, items } groups.
// Both may be mixed; flat entries attach to the trailing anonymous group so
// the model's ordering is preserved.
const parseExercises = (value) => {
  if (!Array.isArray(value) || value.length === 0) return null;

  const groups = [];
  let total = 0;

  for (const entry of value) {
    const isGroup = entry && typeof entry === 'object' && !Array.isArray(entry) && Array.isArray(entry.items);
    if (isGroup) {
      if (entry.items.length === 0) return null;
      const items = entry.items.map(parseExercise);
      if (items.some(item => item === null)) return null;
      groups.push({ section: asTrimmedString(entry.section, 80), items, named: true });
      total += items.length;
    } else {
      const exercise = parseExercise(entry);
      if (!exercise) return null;
      let last = groups[groups.length - 1];
      if (!last || last.named) {
        last = { section: '', items: [], named: false };
        groups.push(last);
      }
      last.items.push(exercise);
      total += 1;
    }

    if (groups.length > MAX_EXERCISE_GROUPS || total > MAX_EXERCISES) return null;
  }

  return {
    groups: groups.map(({ section, items }) => ({ section, items })),
    total,
  };
};

export function parseWorkoutSpec(raw) {
  if (typeof raw !== 'string' || !raw.trim()) return null;

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

    const title = asTrimmedString(parsed.title, 120);
    if (!title) return null;

    const exercises = parseExercises(parsed.exercises);
    if (!exercises) return null;

    const difficulty = asTrimmedString(parsed.difficulty, 20).toLowerCase();

    return {
      title,
      description: asTrimmedString(parsed.description, 400),
      duration: asTrimmedString(parsed.duration, 40),
      difficulty: DIFFICULTIES.has(difficulty) ? difficulty : '',
      rounds: asShortValue(parsed.rounds, 40),
      calories: asTrimmedString(parsed.calories, 40),
      equipment: asOptionalList(parsed.equipment, MAX_EQUIPMENT, 30),
      exerciseGroups: exercises.groups,
      exerciseCount: exercises.total,
      tips: asOptionalList(parsed.tips, MAX_TIPS, 300),
    };
  } catch (_) {
    return null;
  }
}
